import { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Separator } from "../components/ui/separator";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { CheckCircle, Download, Phone, Mail, MapPin, CalendarDays } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const LOGO_URL = "https://customer-assets.emergentagent.com/job_contractor-pipeline/artifacts/xmrl9jcs_image_1764138784069.png";

export default function CustomerApprovalPage() {
  const { estimateId } = useParams();
  const [estimate, setEstimate] = useState(null);
  const [lead, setLead] = useState(null);
  const [loading, setLoading] = useState(true);
  const [approved, setApproved] = useState(false);
  const [error, setError] = useState("");
  const [showDatePicker, setShowDatePicker] = useState(false);
  const [availableDates, setAvailableDates] = useState([]);
  const [selectedDates, setSelectedDates] = useState([]);
  const [customerNotes, setCustomerNotes] = useState("");
  const [dateSubmitted, setDateSubmitted] = useState(false);

  useEffect(() => {
    const fetchEstimate = async () => {
      try {
        const { data: est } = await axios.get(`${API}/estimates/${estimateId}/public`);
        setEstimate(est);
        if (est.lead_id) {
          try {
            const { data: l } = await axios.get(`${API}/leads/${est.lead_id}/public`);
            setLead(l);
          } catch (err) { console.error("Failed to load lead info:", err); }
        }
        if (est.status === "approved") setApproved(true);
      } catch {
        setError("Estimate not found or no longer available.");
      } finally {
        setLoading(false);
      }
    };
    fetchEstimate();
  }, [estimateId]);

  const handleApprove = async () => {
    try {
      await axios.post(`${API}/estimates/${estimateId}/approve`);
      setApproved(true);
      setEstimate((prev) => ({ ...prev, status: "approved" }));
      // Fetch available dates for next 2 months
      const now = new Date();
      const m1 = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}`;
      const next = new Date(now.getFullYear(), now.getMonth() + 1, 1);
      const m2 = `${next.getFullYear()}-${String(next.getMonth() + 1).padStart(2, "0")}`;
      try {
        const [r1, r2] = await Promise.all([
          axios.get(`${API}/bookings/available?month=${m1}`),
          axios.get(`${API}/bookings/available?month=${m2}`),
        ]);
        setAvailableDates([...r1.data.available, ...r2.data.available]);
        setShowDatePicker(true);
      } catch (err) { console.error("Failed to load available dates:", err); }
    } catch {
      setError("Failed to approve. Please contact us directly.");
    }
  };

  const handleSubmitDates = async () => {
    if (selectedDates.length === 0) return;
    try {
      await axios.post(`${API}/bookings/customer-request`, {
        estimate_id: estimateId,
        preferred_dates: selectedDates,
        customer_notes: customerNotes,
      });
      setDateSubmitted(true);
      setShowDatePicker(false);
    } catch (err) {
      setError(err.response?.data?.detail || "Failed to submit dates.");
    }
  };

  const toggleDate = (date) => {
    setSelectedDates((prev) =>
      prev.includes(date) ? prev.filter((d) => d !== date) : prev.length < 3 ? [...prev, date] : prev
    );
  };

  const handleDownloadPdf = async () => {
    try {
      const response = await axios.get(`${API}/estimates/${estimateId}/pdf/public`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = `estimate_${estimateId.slice(0, 8)}.pdf`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch {}
  };

  if (loading) return (
    <div className="min-h-screen flex items-center justify-center bg-[#FAFAFA]">
      <div className="w-10 h-10 border-3 border-orange-600 border-t-transparent rounded-full animate-spin" />
    </div>
  );

  if (error && !estimate) return (
    <div className="min-h-screen flex items-center justify-center bg-[#FAFAFA] p-4">
      <Card className="max-w-md w-full border border-zinc-200 shadow-none">
        <CardContent className="p-8 text-center">
          <img src={LOGO_URL} alt="Logo" className="w-16 h-16 rounded-xl mx-auto mb-4" />
          <p className="text-zinc-600">{error}</p>
          <p className="text-sm text-zinc-400 mt-2">Call us at <a href="tel:+14034645736" className="text-orange-600 font-medium">403-464-5736</a></p>
        </CardContent>
      </Card>
    </div>
  );

  return (
    <div className="min-h-screen bg-[#FAFAFA] py-8 px-4" data-testid="customer-approval-page">
      <div className="max-w-2xl mx-auto space-y-6">
        {/* Header */}
        <div className="text-center">
          <img src={LOGO_URL} alt="Logo" className="w-16 h-16 rounded-xl mx-auto mb-3" />
          <h1 className="text-2xl font-extrabold text-zinc-900">Picture Perfect Fence N Construction Inc.</h1>
          <p className="text-sm text-zinc-500 mt-1">Calgary, AB | 403-464-5736 | ryantibble@fencenconstruction.com</p>
        </div>

        {/* Estimate Card */}
        <Card className="border border-zinc-200 shadow-none">
          <CardContent className="p-6">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h2 className="text-lg font-bold text-zinc-900">Your Estimate</h2>
                <p className="text-xs text-zinc-400">#{estimateId?.slice(0, 8).toUpperCase()} - {new Date(estimate?.created_at).toLocaleDateString()}</p>
              </div>
              <Badge className={`capitalize ${approved ? "bg-green-100 text-green-700" : "bg-orange-100 text-orange-700"}`}>
                {approved ? "Approved" : estimate?.status}
              </Badge>
            </div>

            {lead && (
              <div className="bg-zinc-50 rounded-lg p-3 mb-4">
                <p className="text-sm font-semibold text-zinc-900">{lead.name}</p>
                <div className="flex flex-wrap gap-3 mt-1">
                  {lead.phone && <span className="text-xs text-zinc-500 flex items-center gap-1"><Phone className="w-3 h-3" /> {lead.phone}</span>}
                  {lead.email && <span className="text-xs text-zinc-500 flex items-center gap-1"><Mail className="w-3 h-3" /> {lead.email}</span>}
                  {lead.address && <span className="text-xs text-zinc-500 flex items-center gap-1"><MapPin className="w-3 h-3" /> {lead.address}</span>}
                </div>
              </div>
            )}

            {/* Line Items */}
            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-y-2 text-sm">
                <span className="text-zinc-500">Fence Type</span>
                <span className="text-zinc-900 capitalize font-medium">{estimate?.fence_type?.replace(/_/g, " ")}</span>
                <span className="text-zinc-500">Linear Footage</span>
                <span className="text-zinc-900">{estimate?.linear_footage} ft x {estimate?.height} ft</span>
                <span className="text-zinc-500">Post Type</span>
                <span className="text-zinc-900">{estimate?.post_type}</span>
                {estimate?.num_gates > 0 && <>
                  <span className="text-zinc-500">Gates</span>
                  <span className="text-zinc-900">{estimate?.num_gates}</span>
                </>}
                {estimate?.tear_out && <>
                  <span className="text-zinc-500">Tear Out</span>
                  <span className="text-zinc-900">Yes</span>
                </>}
              </div>

              <Separator />

              <div className="space-y-2 text-sm">
                <div className="flex justify-between"><span className="text-zinc-500">Labor</span><span>${(estimate?.labor_total || 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}</span></div>
                <div className="flex justify-between"><span className="text-zinc-500">Materials</span><span>${(estimate?.material_total || 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}</span></div>
                {estimate?.gates_total > 0 && <div className="flex justify-between"><span className="text-zinc-500">Gates</span><span>${(estimate?.gates_total || 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}</span></div>}
                {estimate?.tear_out_total > 0 && <div className="flex justify-between"><span className="text-zinc-500">Tear Out</span><span>${(estimate?.tear_out_total || 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}</span></div>}
                <Separator />
                <div className="flex justify-between"><span className="text-zinc-500">Subtotal</span><span className="font-medium">${(estimate?.subtotal || 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}</span></div>
                <div className="flex justify-between"><span className="text-zinc-500">GST ({estimate?.tax_rate || 5}%)</span><span>${(estimate?.tax || 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}</span></div>
                <Separator />
                <div className="flex justify-between text-xl font-extrabold">
                  <span className="text-zinc-900">Total</span>
                  <span className="text-orange-600">${(estimate?.total || 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}</span>
                </div>
              </div>
            </div>

            {estimate?.notes && (
              <div className="mt-4 bg-zinc-50 rounded-lg p-3">
                <p className="text-xs font-medium text-zinc-500 uppercase tracking-wider mb-1">Notes</p>
                <p className="text-sm text-zinc-700">{estimate.notes}</p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Actions */}
        <div className="space-y-3">
          {!approved ? (
            <Button
              onClick={handleApprove}
              className="w-full bg-emerald-600 hover:bg-emerald-700 text-white h-14 text-lg font-bold"
              data-testid="customer-approve-btn"
            >
              <CheckCircle className="w-5 h-5 mr-2" /> Approve Estimate & Schedule
            </Button>
          ) : dateSubmitted ? (
            <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-4 text-center">
              <CalendarDays className="w-8 h-8 text-emerald-600 mx-auto mb-2" />
              <p className="text-emerald-700 font-bold">Date Request Submitted!</p>
              <p className="text-sm text-emerald-600 mt-1">We'll confirm your installation date shortly and send you a text. A 50% deposit is required to secure your date.</p>
            </div>
          ) : showDatePicker ? (
            <Card className="border border-emerald-200 shadow-none">
              <CardContent className="p-4 space-y-4">
                <div className="text-center">
                  <CheckCircle className="w-8 h-8 text-emerald-600 mx-auto mb-2" />
                  <p className="text-emerald-700 font-bold">Estimate Approved!</p>
                  <p className="text-sm text-zinc-600 mt-1">Pick up to 3 preferred installation dates:</p>
                </div>
                <div className="grid grid-cols-3 sm:grid-cols-4 gap-2 max-h-[250px] overflow-y-auto">
                  {availableDates.map((date) => {
                    const d = new Date(date + "T12:00:00");
                    const isSelected = selectedDates.includes(date);
                    return (
                      <button
                        key={date}
                        onClick={() => toggleDate(date)}
                        className={`p-2 rounded-lg border text-center transition-all ${
                          isSelected
                            ? "border-emerald-500 bg-emerald-50 ring-2 ring-emerald-400"
                            : "border-zinc-200 hover:border-orange-300"
                        }`}
                        data-testid={`date-pick-${date}`}
                      >
                        <p className="text-xs text-zinc-500">{d.toLocaleDateString("en-US", { weekday: "short" })}</p>
                        <p className="text-lg font-bold text-zinc-900">{d.getDate()}</p>
                        <p className="text-xs text-zinc-500">{d.toLocaleDateString("en-US", { month: "short" })}</p>
                      </button>
                    );
                  })}
                </div>
                {selectedDates.length > 0 && (
                  <p className="text-xs text-emerald-600 text-center">Selected: {selectedDates.map(d => new Date(d + "T12:00:00").toLocaleDateString()).join(", ")}</p>
                )}
                <div className="space-y-2">
                  <Label className="text-sm font-medium text-zinc-700">Any notes for the crew?</Label>
                  <Textarea value={customerNotes} onChange={(e) => setCustomerNotes(e.target.value)} placeholder="Gate access code, dog in yard, etc..." rows={2} />
                </div>
                <Button
                  onClick={handleSubmitDates}
                  disabled={selectedDates.length === 0}
                  className="w-full bg-emerald-600 hover:bg-emerald-700 text-white h-12 font-bold"
                  data-testid="submit-dates-btn"
                >
                  <CalendarDays className="w-4 h-4 mr-2" /> Request {selectedDates.length} Date{selectedDates.length !== 1 ? "s" : ""}
                </Button>
              </CardContent>
            </Card>
          ) : (
            <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-4 text-center">
              <CheckCircle className="w-8 h-8 text-emerald-600 mx-auto mb-2" />
              <p className="text-emerald-700 font-bold">Estimate Approved!</p>
              <p className="text-sm text-emerald-600 mt-1">We'll be in touch shortly to schedule your installation.</p>
            </div>
          )}
          <Button
            onClick={handleDownloadPdf}
            variant="outline"
            className="w-full h-12"
            data-testid="customer-download-btn"
          >
            <Download className="w-4 h-4 mr-2" /> Download PDF Copy
          </Button>
        </div>

        {/* Footer */}
        <div className="text-center text-xs text-zinc-400 space-y-1 pt-4">
          <p>This estimate is valid for 30 days. A 50% deposit is required to schedule.</p>
          <p>Picture Perfect Fence N Construction Inc. - Licensed & Insured - 10 Year Warranty</p>
          <p>Questions? Call Ryan at <a href="tel:+14034645736" className="text-orange-600">403-464-5736</a></p>
        </div>
      </div>
    </div>
  );
}
